import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { validateAddressData, sanitizeInput } from "@/lib/security";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ALL_ISRAELI_CITIES } from '@/lib/shippingConfig';
import { MapPin, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AddressFormData {
  label: string;
  recipient_name: string;
  phone: string;
  street_name: string;
  street_number: string;
  details: string;
  city: string;
}

// For shipping addresses, we don't need recipient_name and phone
export interface ShippingAddressFormData extends Omit<AddressFormData, 'recipient_name' | 'phone'> {
  recipient_name?: string;
  phone?: string;
}

interface AddressFormProps {
  initialData?: Partial<AddressFormData>;
  onSave: (data: AddressFormData) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  title?: string;
  saveButtonText?: string;
  showCancelButton?: boolean;
  type?: 'shipping' | 'billing';
}

const AddressForm = ({ 
  initialData, 
  onSave, 
  onCancel, 
  isSaving, 
  title = 'כתובת',
  saveButtonText = 'שמור כתובת',
  showCancelButton = true,
  type = 'shipping'
}: AddressFormProps) => {
  const [formData, setFormData] = useState<AddressFormData>({
    label: initialData?.label || '',
    recipient_name: initialData?.recipient_name || '',
    phone: initialData?.phone || '',
    street_name: initialData?.street_name || '',
    street_number: initialData?.street_number || '',
    details: initialData?.details || '',
    city: initialData?.city || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [citySearchOpen, setCitySearchOpen] = useState(false);
  const [citySearchValue, setCitySearchValue] = useState(initialData?.city || '');

  // Check if all mandatory fields are filled
  const isFormValid = formData.city && formData.street_name && formData.street_number;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only validate required fields
    if (!formData.street_name) newErrors.street_name = 'שם רחוב נדרש';
    if (!formData.street_number) newErrors.street_number = 'מספר בית נדרש';
    if (!formData.city) newErrors.city = 'עיר נדרשת';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Enhanced security validation
    const validationTarget = type === 'shipping' ? { ...formData, phone: '' } : formData;
    const validation = validateAddressData(validationTarget);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    if (!validateForm()) return;

    // Sanitize and prepare final data
    // For shipping addresses, don't include phone and recipient_name
    const finalData = {
      ...formData,
      label: sanitizeInput(`${formData.street_name} ${formData.street_number}, ${formData.city}`),
      recipient_name: type === 'billing' ? sanitizeInput(formData.recipient_name || '') : '',
      phone: type === 'billing' ? (formData.phone || '') : '',
      street_name: sanitizeInput(formData.street_name),
      street_number: sanitizeInput(formData.street_number),
      details: sanitizeInput(formData.details),
      city: sanitizeInput(formData.city)
    };

    try {
      await onSave(finalData);
      toast.success('כתובת נשמרה בהצלחה');
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('שגיאה בשמירת הכתובת');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" dir="rtl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="city">
            <span className="text-destructive">*</span> עיר
          </Label>
          <Popover open={citySearchOpen} onOpenChange={setCitySearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={citySearchOpen}
                className={cn(
                  "w-full justify-between h-11 text-base font-medium",
                  errors.city && 'border-red-500'
                )}
              >
                {formData.city || "בחר עיר..."}
                <MapPin className="ml-2 h-5 w-5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-full lg:w-[400px] p-0" align="start" sideOffset={5}>
              <Command>
                <CommandInput
                  placeholder="חפש עיר..."
                  value={citySearchValue}
                  onValueChange={setCitySearchValue}
                  dir="rtl"
                />
                <CommandList>
                  <CommandEmpty>לא נמצאו ערים</CommandEmpty>
                  <CommandGroup className="max-h-[300px] overflow-y-auto">
                    {ALL_ISRAELI_CITIES
                      .filter(city =>
                        citySearchValue.length < 2
                          ? false
                          : city.includes(citySearchValue)
                      )
                      .slice(0, 50)
                      .map((city) => (
                        <CommandItem
                          key={city}
                          value={city}
                          onSelect={(currentValue) => {
                            setFormData(prev => ({ ...prev, city: currentValue }));
                            setCitySearchValue(currentValue);
                            setCitySearchOpen(false);
                            if (errors.city) {
                              setErrors(prev => ({ ...prev, city: '' }));
                            }
                          }}
                        >
                          <Check
                            className={cn(
                              "ml-2 h-4 w-4",
                              formData.city === city ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {city}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {errors.city && (
            <p className="text-red-500 text-sm">{errors.city}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="street_name">
            <span className="text-destructive">*</span> שם רחוב
          </Label>
          <Input
            id="street_name"
            name="street_name"
            value={formData.street_name}
            onChange={handleInputChange}
            className={errors.street_name ? 'border-red-500' : ''}
            dir="rtl"
          />
          {errors.street_name && (
            <p className="text-red-500 text-sm">{errors.street_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="street_number">
            <span className="text-destructive">*</span> מספר בית
          </Label>
          <Input
            id="street_number"
            name="street_number"
            value={formData.street_number}
            onChange={handleInputChange}
            className={errors.street_number ? 'border-red-500' : ''}
            dir="rtl"
          />
          {errors.street_number && (
            <p className="text-red-500 text-sm">{errors.street_number}</p>
          )}
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="details">פרטים נוספים</Label>
          <Input
            id="details"
            name="details"
            value={formData.details}
            onChange={handleInputChange}
            placeholder="קומה, דלת, קוד כניסה וכו'"
            dir="rtl"
          />
        </div>
      </div>

      {/* ברירת מחדל הוסרה - המערכת זוכרת את הכתובת האחרונה שנבחרה */}

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={isSaving || !isFormValid}>
          {isSaving ? 'שומר...' : saveButtonText}
        </Button>
        {showCancelButton && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ביטול
          </Button>
        )}
      </div>
    </form>
  );
};

export default AddressForm;